import React, { useState } from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { 
  LayoutGrid, 
  Maximize, 
  Settings2, 
  FileArchive, 
  Scissors, 
  Zap, 
  Box, 
  ChevronDown, 
  X, 
  SlidersHorizontal,
  Palette,
  ShieldCheck,
  Sparkles,
  Info,
  Layers,
  RotateCw,
  AlignCenter
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { formatUnit, parseUnit } from '../../utils/units';
import { formatPrintSlug } from '../../utils/slugFormatter';
import { useToast } from '../../store/ToastContext';
import { ImpositionPreview } from './ImpositionPreview';
import { RoiCalculatorPanel } from './RoiCalculatorPanel';
import { TrimBoxMediaBoxPanel } from './TrimBoxMediaBoxPanel';
import { ColorBarsConfigPanel } from './ColorBarsConfigPanel';
import { PlotterFinishingPanel } from './PlotterFinishingPanel';
import { GangingAndNestingPanel } from './GangingAndNestingPanel';
import { RollLabelModePanel } from './RollLabelModePanel';
import { PressPresetsBar } from './PressPresetsBar';
import { ImpositionThumbnailPreview } from './ImpositionThumbnailPreview';
import { DraggableFloatingWindow } from './DraggableFloatingWindow';
import { Disc } from 'lucide-react';

export const ImpositionStep = () => {
  const { layout, setLayout, template, appUnit, csvData } = useSaaSContext();
  const { toast } = useToast();

  // Left Drawer Open/Close state (Default: CLOSED when page opens)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Floating thumbnail preview state
  const [showFloatingThumbnail, setShowFloatingThumbnail] = useState(false);

  // Accordion Sections Expand/Collapse State (Default: ALL COLLAPSED when drawer opens)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    exportMode: false,
    sheetSize: false,
    grid: false,
    trimMarks: false,
    cmyk: false,
    safeZone: false,
    productionMarks: false,
    trimBoxMediaBox: false,
    summary: false,
  });

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleAutoOptimizeGrid = () => {
    const bleedPt = layout.bleed ?? 0;
    const itemWPt = (template.width || 300) + (bleedPt * 2);
    const itemHPt = (template.height || 300) + (bleedPt * 2);

    const marginTop = layout.marginTop ?? 14.17;
    const marginLeft = layout.marginLeft ?? 14.17;
    const marginRight = layout.marginRight ?? 14.17;
    const marginBottom = layout.marginBottom ?? 14.17;

    const wPt = layout.pageWidth || 595.28;
    const hPt = layout.pageHeight || 841.89;

    const availW = wPt - (marginLeft + marginRight);
    const availH = hPt - (marginTop + marginBottom);

    const calcCols = Math.max(1, Math.floor(availW / itemWPt));
    const calcRows = Math.max(1, Math.floor(availH / itemHPt));

    setLayout(prev => ({
      ...prev,
      columns: calcCols,
      rows: calcRows
    }));

    toast.success(`Grid auto-optimized to ${calcCols}×${calcRows} (${calcCols * calcRows}-Up)`);
  };

  const handleUpdate = (key: keyof typeof layout, value: any) => {
    setLayout((prev) => ({ ...prev, [key]: value }));
  };

  const handleBatchUpdate = (updates: Partial<typeof layout>) => {
    setLayout((prev) => ({ ...prev, ...updates }));
  };

  const rawSheetWidth = layout.pageSize === 'custom' && layout.pageWidth ? Number(layout.pageWidth) : (layout.pageSize === 'A3' ? 842 : 595);
  const rawSheetHeight = layout.pageSize === 'custom' && layout.pageHeight ? Number(layout.pageHeight) : (layout.pageSize === 'A3' ? 1190 : 842);
  const isLandscape = layout.pageOrientation === 'landscape';
  const finalSheetWidth = isLandscape ? rawSheetHeight : rawSheetWidth;
  const finalSheetHeight = isLandscape ? rawSheetWidth : rawSheetHeight;

  const cols = Number(layout.cols) || 1;
  const rows = Number(layout.rows) || 1;

  const handleAutoCalculate = () => {
    const availW = finalSheetWidth - (layout.marginX * 2);
    const availH = finalSheetHeight - (layout.marginY * 2);
    
    const bld = layout.bleed || 0;
    const wWithBleed = template.width + (bld * 2);
    const hWithBleed = template.height + (bld * 2);
    
    // Normal Orientation
    const newCols1 = Math.floor((availW + layout.gapX) / (wWithBleed + layout.gapX));
    const newRows1 = Math.floor((availH + layout.gapY) / (hWithBleed + layout.gapY));
    const total1 = Math.max(0, newCols1) * Math.max(0, newRows1);
    
    // Rotated 90 Orientation
    const newCols2 = Math.floor((availW + layout.gapX) / (hWithBleed + layout.gapX));
    const newRows2 = Math.floor((availH + layout.gapY) / (wWithBleed + layout.gapY));
    const total2 = Math.max(0, newCols2) * Math.max(0, newRows2);
    
    if (total1 === 0 && total2 === 0) {
      toast.error("Layout Warning", "Template size plus margins is too large to fit even one on the sheet.");
      return;
    }
    
    // Auto-Nest Optimizer
    if (total2 > total1) {
      setLayout({ ...layout, cols: newCols2, rows: newRows2, rotateStickers: true });
      toast.success("Layout Optimized! ✨", `Rotated stickers to fit ${total2} per sheet (was ${total1}).`);
    } else {
      setLayout({ ...layout, cols: newCols1, rows: newRows1, rotateStickers: false });
      toast.success("Already Optimal ✨", `Current layout is already the most efficient (${total1} per sheet).`);
    }
  };

  const handleCenterOnPage = () => {
    const bld = layout.bleed || 0;
    const rawStickerWidth = template.width + (bld * 2);
    const rawStickerHeight = template.height + (bld * 2);
    
    const stickerWidth = layout.rotateStickers ? rawStickerHeight : rawStickerWidth;
    const stickerHeight = layout.rotateStickers ? rawStickerWidth : rawStickerHeight;

    const totalW = (stickerWidth * cols) + (layout.gapX * (cols - 1));
    const totalH = (stickerHeight * rows) + (layout.gapY * (rows - 1));
    
    const newMarginX = Math.max(0, (finalSheetWidth - totalW) / 2);
    const newMarginY = Math.max(0, (finalSheetHeight - totalH) / 2);
    
    setLayout({ ...layout, marginX: newMarginX, marginY: newMarginY });
  };

  const isSingleUp = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs';

  return (
    <div className="w-full h-full flex-1 flex flex-col min-h-0 max-w-[1920px] mx-auto overflow-hidden">
      {/* Main Imposition Workspace - Full Screen Height & Width with Single Unified Header Toolbar */}
      <div className="w-full flex-1 min-h-0 relative flex flex-col">
        <ImpositionPreview 
          onToggleDrawer={() => setIsDrawerOpen(prev => !prev)}
          isDrawerOpen={isDrawerOpen}
          onToggleFloatingThumbnail={() => setShowFloatingThumbnail(prev => !prev)}
          showFloatingThumbnail={showFloatingThumbnail}
        />
      </div>

      {/* Slide-Over Drawer for Properties */}
      <AnimatePresence>
        {isDrawerOpen && (
          <motion.div 
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300, mass: 0.8 }}
            className="fixed top-0 left-0 bottom-0 h-full w-[320px] sm:w-[380px] md:w-[440px] lg:w-[480px] bg-zinc-900/95 backdrop-blur-md border-r border-zinc-800 shadow-2xl z-50 flex flex-col"
          >
            {/* Drawer Header */}
            <div className="p-4 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 text-zinc-100 font-semibold text-base">
                <SlidersHorizontal className="w-5 h-5 text-blue-500" />
                <span>Imposition Properties</span>
              </div>
              <button 
                onClick={() => setIsDrawerOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 rounded-lg transition-colors cursor-pointer"
                title="Close Drawer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Body - Collapsible Accordions with drafting dot pattern */}
            <div 
              className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar"
              style={{
                backgroundImage: 'radial-gradient(circle, rgba(255, 255, 255, 0.08) 1px, transparent 1px)',
                backgroundSize: '16px 16px'
              }}
            >

              {/* Real-Time Interactive Imposition Grid Thumbnail */}
              <div className="mb-4">
                <ImpositionThumbnailPreview />
              </div>

              {/* Quick Setup Presets */}
              <div className="mb-Quick Setup Presets">
                <label className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Quick Setup Presets</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleBatchUpdate({ exportMode: 'multi_up', pageSize: 'A3', marginX: 10, marginY: 10, gapX: 2, gapY: 2, bleed: 2, showTrimMarks: true, trimMarkType: 'outer_grid', colorBars: true, printSlug: true })}
                    className="p-2.5 bg-zinc-900 hover:bg-blue-950/60 border border-zinc-800 hover:border-blue-700/50 rounded-lg text-left transition-colors cursor-pointer group"
                  >
                    <div className="text-xs font-semibold text-zinc-200 group-hover:text-blue-400">Commercial Press</div>
                    <div className="text-[10px] text-zinc-500 mt-1 leading-tight">A3, Bleed, Marks, Slug</div>
                  </button>
                  <button
                    onClick={() => handleBatchUpdate({ exportMode: 'multi_up', pageSize: 'A4', marginX: 5, marginY: 5, gapX: 0, gapY: 0, bleed: 0, showTrimMarks: false, colorBars: false, printSlug: false })}
                    className="p-2.5 bg-zinc-900 hover:bg-emerald-950/60 border border-zinc-800 hover:border-emerald-700/50 rounded-lg text-left transition-colors cursor-pointer group"
                  >
                    <div className="text-xs font-semibold text-zinc-200 group-hover:text-emerald-400">Office Printer</div>
                    <div className="text-[10px] text-zinc-500 mt-1 leading-tight">A4, No Bleed, Basic Grid</div>
                  </button>
                  <button
                    onClick={() => handleBatchUpdate({ exportMode: 'single_pdf', bleed: 0, showTrimMarks: false, colorBars: false, printSlug: false })}
                    className="p-2.5 bg-zinc-900 hover:bg-purple-950/60 border border-zinc-800 hover:border-purple-700/50 rounded-lg text-left transition-colors cursor-pointer group"
                  >
                    <div className="text-xs font-semibold text-zinc-200 group-hover:text-purple-400">Digital (Web)</div>
                    <div className="text-[10px] text-zinc-500 mt-1 leading-tight">Single PDF, Exact Size</div>
                  </button>
                  <button
                    onClick={() => handleBatchUpdate({ exportMode: 'separate_pdfs', bleed: 2, showTrimMarks: true, trimMarkType: 'all_marks', colorBars: false, printSlug: false })}
                    className="p-2.5 bg-zinc-900 hover:bg-amber-950/60 border border-zinc-800 hover:border-amber-700/50 rounded-lg text-left transition-colors cursor-pointer group"
                  >
                    <div className="text-xs font-semibold text-zinc-200 group-hover:text-amber-400">Batch Export (.zip)</div>
                    <div className="text-[10px] text-zinc-500 mt-1 leading-tight">Individual PDFs + Marks</div>
                  </button>
                </div>
              </div>

              {/* ... (Existing sections below) ... */}

          {/* Section 1: Export Mode & Nesting Engine */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('exportMode')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <FileArchive className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Export Mode & Nesting</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['exportMode'] ? 'rotate-180 text-blue-400' : ''}`} />
            </button>

            {openSections['exportMode'] && (
              <div className="p-3.5 space-y-4 animate-in fade-in duration-150">
                <div>
                  <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Export Mode</label>
                  <select 
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600"
                    value={layout.exportMode || 'multi_up'}
                    onChange={(e) => handleUpdate('exportMode', e.target.value)}
                  >
                    <option value="multi_up">Multi-Up Sheet (Imposition)</option>
                    <option value="single_pdf">Single PDF (1 per page)</option>
                    <option value="separate_pdfs">Batch / Separate PDFs (.zip)</option>
                    <option value="separate_multi_up_pdfs">Batch / Multi-Up Sheets (.zip)</option>
                  </select>
                </div>

                {!isSingleUp && (
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1.5 block">Nesting Engine (Data Flow)</label>
                    <select 
                      className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600 mb-2"
                      value={layout.nestingMode || 'variable'}
                      onChange={(e) => handleUpdate('nestingMode', e.target.value)}
                    >
                      <option value="variable">Variable Data (1 record per cell)</option>
                      <option value="step_and_repeat">Step & Repeat (Fill sheet with same record)</option>
                    </select>
                    
                    {layout.nestingMode === 'step_and_repeat' && (
                      <div className="mb-2">
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Copies per Record</label>
                        <input 
                          type="number" min="1" placeholder="e.g. 100"
                          className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600 text-xs"
                          value={layout.quantityPerRecord || ''}
                          onChange={(e) => handleUpdate('quantityPerRecord', parseInt(e.target.value) || undefined)}
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Direction</label>
                        <select 
                          className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600 text-xs"
                          value={layout.nestingDirection || 'row_first'}
                          onChange={(e) => handleUpdate('nestingDirection', e.target.value)}
                        >
                          <option value="row_first">Row First (L→R)</option>
                          <option value="col_first">Col First (T→B)</option>
                          <option value="cut_stack">Cut Stack (Z-Axis)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-zinc-500 mb-1 block">Bleed ({appUnit})</label>
                        <input 
                          type="number" min="0" placeholder="0"
                          className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600 text-xs font-mono"
                          value={layout.bleed ? formatUnit(layout.bleed, appUnit) : ''}
                          onChange={(e) => handleUpdate('bleed', parseUnit(e.target.value, appUnit))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 2: Target Sheet Size & Orientation */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60" id="tour-imposition-header">
            <button 
              type="button"
              onClick={() => toggleSection('sheetSize')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Maximize className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Target Sheet Size</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['sheetSize'] ? 'rotate-180 text-emerald-400' : ''}`} />
            </button>

            {openSections['sheetSize'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <PressPresetsBar
                  layout={layout}
                  template={template}
                  onUpdateLayout={handleUpdate}
                  onAutoOptimizeGrid={handleAutoOptimizeGrid}
                />

                <div>
                  <label className="text-xs font-medium text-zinc-400 mb-1 block">Preset Sheet Size</label>
                  <select 
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600 text-xs"
                    value={layout.pageSize}
                    onChange={(e) => handleUpdate('pageSize', e.target.value)}
                  >
                    <option value="12x18">12" × 18" Digital Press (304.8 × 457.2 mm)</option>
                    <option value="13x19">13" × 19" Super A3 / SRA3 (330 × 483 mm)</option>
                    <option value="18x24">18" × 24" Medium Format Press Sheet</option>
                    <option value="24roll">24" Vinyl Roll Continuous</option>
                    <option value="36roll">36" Vinyl Roll Continuous</option>
                    <option value="A4">A4 (210 x 297 mm)</option>
                    <option value="A3">A3 (297 x 420 mm)</option>
                    <option value="custom">Custom Size</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-400 mb-1 block">Orientation</label>
                  <select 
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 outline-none focus:ring-2 focus:ring-blue-600 text-xs"
                    value={layout.pageOrientation || 'portrait'}
                    onChange={(e) => handleUpdate('pageOrientation', e.target.value)}
                  >
                    <option value="portrait">Portrait</option>
                    <option value="landscape">Landscape</option>
                  </select>
                </div>

                {layout.pageSize === 'custom' && (
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <label className="text-[11px] text-zinc-500 mb-1 block">Width ({appUnit})</label>
                      <input 
                        type="number" min="1" placeholder="Width"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 text-xs font-mono outline-none focus:ring-2 focus:ring-blue-600"
                        value={layout.pageWidth ? formatUnit(layout.pageWidth, appUnit) : ''}
                        onChange={(e) => handleUpdate('pageWidth', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-zinc-500 mb-1 block">Height ({appUnit})</label>
                      <input 
                        type="number" min="1" placeholder="Height"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 text-xs font-mono outline-none focus:ring-2 focus:ring-blue-600"
                        value={layout.pageHeight ? formatUnit(layout.pageHeight, appUnit) : ''}
                        onChange={(e) => handleUpdate('pageHeight', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 3: Grid Layout & Margins */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60" id="tour-imposition-layout-controls">
            <button 
              type="button"
              onClick={() => toggleSection('grid')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <LayoutGrid className="w-4 h-4 text-purple-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Grid Rows, Cols & Margins</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['grid'] ? 'rotate-180 text-purple-400' : ''}`} />
            </button>

            {openSections['grid'] && (
              <div className="p-3.5 space-y-3.5 animate-in fade-in duration-150">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Rows</label>
                    <input 
                      type="number" 
                      min="1" max="100"
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                      value={layout.rows || ''}
                      onChange={(e) => handleUpdate('rows', e.target.value === '' ? '' : parseInt(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Columns</label>
                    <input 
                      type="number" 
                      min="1" max="100"
                      className="w-full px-3 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                      value={layout.cols || ''}
                      onChange={(e) => handleUpdate('cols', e.target.value === '' ? '' : parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Gap X / Y ({appUnit})</label>
                    <div className="flex gap-1.5">
                      <input 
                        type="number" min="0" placeholder="X"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                        value={layout.gapX !== undefined && !isNaN(layout.gapX) ? formatUnit(layout.gapX, appUnit) : ''}
                        onChange={(e) => handleUpdate('gapX', parseUnit(e.target.value, appUnit))}
                      />
                      <input 
                        type="number" min="0" placeholder="Y"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                        value={layout.gapY !== undefined && !isNaN(layout.gapY) ? formatUnit(layout.gapY, appUnit) : ''}
                        onChange={(e) => handleUpdate('gapY', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-zinc-400 mb-1 block">Margin X / Y ({appUnit})</label>
                    <div className="flex gap-1.5">
                      <input 
                        type="number" min="0" placeholder="X"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                        value={layout.marginX !== undefined && !isNaN(layout.marginX) ? formatUnit(layout.marginX, appUnit) : ''}
                        onChange={(e) => handleUpdate('marginX', parseUnit(e.target.value, appUnit))}
                      />
                      <input 
                        type="number" min="0" placeholder="Y"
                        className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 font-mono text-xs"
                        value={layout.marginY !== undefined && !isNaN(layout.marginY) ? formatUnit(layout.marginY, appUnit) : ''}
                        onChange={(e) => handleUpdate('marginY', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 4: Trim Marks & Cutting Ticks */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60" id="tour-imposition-marks-toggle">
            <button 
              type="button"
              onClick={() => toggleSection('trimMarks')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Scissors className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Trim Marks & Cutting Ticks</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['trimMarks'] ? 'rotate-180 text-amber-400' : ''}`} />
            </button>

            {openSections['trimMarks'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <select 
                  className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-blue-600 text-xs"
                  value={layout.showTrimMarks ? (layout.trimMarkType || 'outer_grid') : 'none'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'none') {
                      handleBatchUpdate({ showTrimMarks: false, trimMarkType: 'none' });
                    } else {
                      handleBatchUpdate({ 
                        showTrimMarks: true, 
                        trimMarkType: val as any,
                        trimMarkOffset: layout.trimMarkOffset !== undefined ? layout.trimMarkOffset : 8.5,
                        trimMarkLength: layout.trimMarkLength !== undefined ? layout.trimMarkLength : 10
                      });
                    }
                  }}
                >
                  <option value="none">None (No Trim Marks)</option>
                  <option value="outer_grid">Outer Grid Only (Clean Outside Marks - Commercial)</option>
                  <option value="corner_marks">Corner Marks Only (4 Corners)</option>
                  <option value="individual">Per-Sticker Individual Marks (Each Sticker)</option>
                  <option value="all_marks">All Marks (Outer Grid + Corner Marks)</option>
                </select>

                {layout.showTrimMarks && (
                  <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-900/90 border border-zinc-800 rounded-lg">
                    <div>
                      <label className="text-xs font-medium text-zinc-400 mb-1 block">Offset ({appUnit})</label>
                      <input 
                        type="number" step="0.5" min="0" max="50"
                        className="w-full px-2.5 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded-md text-xs font-mono outline-none focus:ring-1 focus:ring-blue-500"
                        value={layout.trimMarkOffset !== undefined && !isNaN(layout.trimMarkOffset) ? formatUnit(layout.trimMarkOffset, appUnit) : ''}
                        onChange={(e) => handleUpdate('trimMarkOffset', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-400 mb-1 block">Line Length ({appUnit})</label>
                      <input 
                        type="number" step="0.5" min="1" max="50"
                        className="w-full px-2.5 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded-md text-xs font-mono outline-none focus:ring-1 focus:ring-blue-500"
                        value={layout.trimMarkLength !== undefined && !isNaN(layout.trimMarkLength) ? formatUnit(layout.trimMarkLength, appUnit) : ''}
                        onChange={(e) => handleUpdate('trimMarkLength', parseUnit(e.target.value, appUnit))}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 5: Commercial Print Profile (CMYK) */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('cmyk')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Sparkles className="w-4 h-4 text-pink-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Commercial Print Profile (CMYK)</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['cmyk'] ? 'rotate-180 text-pink-400' : ''}`} />
            </button>

            {openSections['cmyk'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <label className="flex items-start gap-3 p-3 bg-zinc-900 border border-zinc-800 rounded-lg cursor-pointer hover:bg-zinc-800/80 text-xs">
                  <input 
                    type="checkbox" 
                    className="mt-0.5 rounded accent-blue-600"
                    checked={layout.useCmyk || false}
                    onChange={(e) => handleUpdate('useCmyk', e.target.checked)}
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold text-zinc-200">Export as CMYK Color Space</span>
                    <span className="text-[11px] text-zinc-400 leading-normal mt-1">Converts RGB colors to CMYK space for offset and commercial printing presses to prevent shift in final output.</span>
                  </div>
                </label>
              </div>
            )}
          </div>

          {/* Section 6: Safe Zone (Inner Margin) */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('safeZone')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Safe Zone (Inner Margin)</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['safeZone'] ? 'rotate-180 text-emerald-400' : ''}`} />
            </button>

            {openSections['safeZone'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-xs text-zinc-200 font-semibold cursor-pointer select-none">
                    <input 
                      type="checkbox"
                      className="rounded border-zinc-700 accent-emerald-500"
                      checked={layout.showSafeZone !== false}
                      onChange={e => handleUpdate('showSafeZone', e.target.checked)}
                    />
                    <span>Safe Zone Active</span>
                  </label>
                  <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
                    {formatUnit(layout.safeZone !== undefined ? layout.safeZone : 8.5, appUnit)} {appUnit}
                  </span>
                </div>

                <div className="flex gap-2 items-center">
                  <input 
                    type="number" min="0" step="0.5"
                    className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 text-xs font-mono"
                    value={layout.safeZone !== undefined ? formatUnit(layout.safeZone, appUnit) : ''}
                    onChange={(e) => handleUpdate('safeZone', parseUnit(e.target.value, appUnit))}
                  />
                  <div className="flex gap-1 shrink-0">
                    <button 
                      type="button"
                      onClick={() => handleUpdate('safeZone', 5.67)} 
                      className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-[10px] font-medium text-zinc-300 rounded-md border border-zinc-700 cursor-pointer"
                    >
                      2mm
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleUpdate('safeZone', 8.5)} 
                      className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 text-[10px] font-medium text-zinc-300 rounded-md border border-zinc-700 cursor-pointer"
                    >
                      3mm
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Production Marks & Color Bars */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('productionMarks')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Settings2 className="w-4 h-4 text-blue-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Production Marks & Slug</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['productionMarks'] ? 'rotate-180 text-blue-400' : ''}`} />
            </button>

            {openSections['productionMarks'] && (
              <div className="p-3.5 space-y-4 animate-in fade-in duration-150">
                {/* Slug */}
                <div className="space-y-2">
                  <label className="flex items-center justify-between text-xs text-zinc-200 font-semibold cursor-pointer">
                    <span className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        className="rounded border-zinc-700 accent-blue-600"
                        checked={layout.printSlug || false}
                        onChange={e => {
                          const checked = e.target.checked;
                          handleBatchUpdate({
                            printSlug: checked,
                            printSlugPosition: layout.printSlugPosition || 'bottom_left',
                            printSlugOffset: layout.printSlugOffset !== undefined ? layout.printSlugOffset : 8.5
                          });
                        }}
                      />
                      Print Slug (Job Info)
                    </span>
                  </label>

                  {layout.printSlug && (
                    <div className="space-y-2.5 p-3 bg-zinc-900 border border-zinc-800 rounded-lg">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Position</label>
                          <select
                            className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded text-xs outline-none"
                            value={layout.printSlugPosition || 'bottom_left'}
                            onChange={e => handleUpdate('printSlugPosition', e.target.value)}
                          >
                            <option value="bottom_left">Bottom Left</option>
                            <option value="bottom_center">Bottom Center</option>
                            <option value="bottom_right">Bottom Right</option>
                            <option value="top_left">Top Left</option>
                            <option value="top_center">Top Center</option>
                            <option value="top_right">Top Right</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[11px] font-medium text-zinc-400 mb-1 block">Offset ({appUnit})</label>
                          <input 
                            type="number" step="0.5" min="0" max="100"
                            className="w-full px-2 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded text-xs font-mono outline-none"
                            value={layout.printSlugOffset !== undefined && !isNaN(layout.printSlugOffset) ? formatUnit(layout.printSlugOffset, appUnit) : ''}
                            onChange={e => handleUpdate('printSlugOffset', parseUnit(e.target.value, appUnit))}
                          />
                        </div>
                      </div>

                      {/* Slug Name / Source Selection */}
                      <div className="pt-2 border-t border-zinc-800/80">
                        <label className="text-[11px] font-medium text-zinc-400 mb-1.5 block">Job Format</label>
                        <div className="grid grid-cols-3 gap-1 p-0.5 bg-zinc-950 border border-zinc-800 rounded-md">
                          <button
                            type="button"
                            onClick={() => handleUpdate('printSlugSource', 'default')}
                            className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                              (!layout.printSlugSource || layout.printSlugSource === 'default')
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            Default
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdate('printSlugSource', 'custom')}
                            className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                              layout.printSlugSource === 'custom'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            Custom Text
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdate('printSlugSource', 'column')}
                            className={`px-2 py-1 text-[11px] font-medium rounded transition-colors ${
                              layout.printSlugSource === 'column'
                                ? 'bg-blue-600 text-white shadow-xs'
                                : 'text-zinc-400 hover:text-zinc-200'
                            }`}
                          >
                            MAP Column
                          </button>
                        </div>

                        {layout.printSlugSource === 'custom' && (
                          <div className="mt-2 space-y-1.5">
                            <input
                              type="text"
                              className="w-full px-2.5 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded text-xs outline-none font-mono"
                              placeholder="e.g. Batch #401 - High Priority"
                              value={layout.printSlugText || ''}
                              onChange={e => handleUpdate('printSlugText', e.target.value)}
                            />
                          </div>
                        )}

                        {layout.printSlugSource === 'column' && (
                          <div className="mt-2">
                            {csvData?.headers && csvData.headers.length > 0 ? (
                              <select
                                className="w-full px-2.5 py-1.5 bg-zinc-950 border border-zinc-700/80 text-zinc-100 rounded text-xs outline-none font-medium"
                                value={layout.printSlugColumn || ''}
                                onChange={e => handleUpdate('printSlugColumn', e.target.value)}
                              >
                                <option value="">-- Select Column --</option>
                                {csvData.headers.map(col => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                            ) : (
                              <div className="p-2 bg-amber-950/30 border border-amber-800/40 rounded text-[11px] text-amber-300">
                                Upload CSV in Data step to map column.
                              </div>
                            )}
                          </div>
                        )}

                        <div className="mt-2.5 p-2 bg-zinc-950 border border-zinc-800 rounded text-[10px] font-mono text-zinc-400 break-all">
                          <span className="text-zinc-500 font-semibold block text-[9px] uppercase tracking-wider mb-0.5">Live Output:</span>
                          <span className="text-blue-300">
                            {formatPrintSlug(layout, csvData?.rows?.[0], {
                              sheetIndex: 1,
                              totalSheets: Math.max(1, Math.ceil((csvData?.rows?.length || 1) / ((layout.rows || 1) * (layout.cols || 1)))),
                              totalRecords: csvData?.rows?.length || 1,
                              pageSize: layout.pageSize
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Color Bars */}
                <div className="pt-2 border-t border-zinc-800/80">
                  <label className="flex items-center justify-between text-xs text-zinc-200 font-semibold cursor-pointer mb-2">
                    <span className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        className="rounded border-zinc-700 accent-blue-600"
                        checked={layout.colorBars || false}
                        onChange={e => {
                          const checked = e.target.checked;
                          handleBatchUpdate({
                            colorBars: checked,
                            colorBarsPosition: layout.colorBarsPosition || 'top_center',
                            colorBarsOffset: layout.colorBarsOffset !== undefined ? layout.colorBarsOffset : 8.5
                          });
                        }}
                      />
                      <Palette className="w-4 h-4 text-blue-400" />
                      CMYK & Custom Color Bars
                    </span>
                  </label>

                  {layout.colorBars && (
                    <div className="mt-2">
                      <ColorBarsConfigPanel />
                    </div>
                  )}
                </div>

                {/* Registration Marks */}
                <div className="pt-2 border-t border-zinc-800/80">
                  <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="rounded border-zinc-700 accent-blue-600"
                      checked={layout.registrationMarks || false}
                      onChange={e => handleUpdate('registrationMarks', e.target.checked)}
                    />
                    Registration Marks (Crosshairs)
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Section 8: Prepress Trim Box & Media Box */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('trimBoxMediaBox')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Box className="w-4 h-4 text-cyan-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Prepress Trim & Media Box</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['trimBoxMediaBox'] ? 'rotate-180 text-cyan-400' : ''}`} />
            </button>

            {openSections['trimBoxMediaBox'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <TrimBoxMediaBoxPanel />
              </div>
            )}
          </div>

          {/* Section 9: Cutting Plotter & Die-Cut Finishing */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('plotterFinishing')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Scissors className="w-4 h-4 text-pink-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Cutting Plotter & Die-Cut</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['plotterFinishing'] ? 'rotate-180 text-pink-400' : ''}`} />
            </button>

            {openSections['plotterFinishing'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <PlotterFinishingPanel />
              </div>
            )}
          </div>

          {/* Section 10: Multi-Design Ganging & Work-and-Turn Imposition (Phase 3) */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('gangingNesting')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Zap className="w-4 h-4 text-purple-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Multi-Job Ganging & Work-and-Turn</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['gangingNesting'] ? 'rotate-180 text-purple-400' : ''}`} />
            </button>

            {openSections['gangingNesting'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <GangingAndNestingPanel />
              </div>
            )}
          </div>

          {/* Section 11: Continuous Roll Label Mode & Unwind Chart (Phase 4) */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('rollMode')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Disc className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Continuous Roll & Unwind Direction</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['rollMode'] ? 'rotate-180 text-amber-400' : ''}`} />
            </button>

            {openSections['rollMode'] && (
              <div className="p-3.5 space-y-3 animate-in fade-in duration-150">
                <RollLabelModePanel />
              </div>
            )}
          </div>

          {/* Section 9: Sheet & Imposition Summary */}
          <div className="border border-zinc-800 rounded-xl overflow-hidden bg-zinc-950/60">
            <button 
              type="button"
              onClick={() => toggleSection('summary')}
              className="w-full p-3 bg-zinc-950 hover:bg-zinc-900 border-b border-zinc-800/80 flex items-center justify-between text-left transition-colors font-medium text-sm text-zinc-200 cursor-pointer"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Info className="w-4 h-4 text-indigo-400 shrink-0" />
                <span className="font-semibold text-zinc-100 truncate">Sheet Imposition Summary</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform duration-200 shrink-0 ${openSections['summary'] ? 'rotate-180 text-indigo-400' : ''}`} />
            </button>

            {openSections['summary'] && (
              <div className="p-3.5 space-y-2 text-xs text-zinc-300 animate-in fade-in duration-150 font-mono">
                <p><strong>Stickers per sheet:</strong> {rows * cols} Up</p>
                <p><strong>Individual Size:</strong> {template.width !== undefined && !isNaN(template.width) ? formatUnit(template.width, appUnit) : 0} x {template.height !== undefined && !isNaN(template.height) ? formatUnit(template.height, appUnit) : 0} {appUnit}</p>
                <p><strong>Sheet Size:</strong> {formatUnit(finalSheetWidth, appUnit)} x {formatUnit(finalSheetHeight, appUnit)} {appUnit}</p>
              </div>
            )}
          </div>

        </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Draggable Imposition Grid Thumbnail Window */}
      {showFloatingThumbnail && (
        <DraggableFloatingWindow
          title="Interactive Imposition Thumbnail"
          icon={Layers}
          badge="Live Sync"
          badgeColor="purple"
          initialX={Math.max(20, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 460)}
          initialY={140}
          widthClass="w-96 sm:w-[420px]"
          isOpen={showFloatingThumbnail}
          onClose={() => setShowFloatingThumbnail(false)}
        >
          <div className="p-3">
            <ImpositionThumbnailPreview />
          </div>
        </DraggableFloatingWindow>
      )}
    </div>
  );
};
